import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, MessageCircle, Cog } from "lucide-react";
import { BRAND } from "@/lib/brand";

const PILLARS = [
  "Engenharia de Suprimentos",
  "Nacionalização de Componentes",
  "Importação Especializada",
  "Continuidade Operacional",
];

export const Hero = () => {
  const [pillar, setPillar] = useState(0);
  const wrapRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setPillar((p) => (p + 1) % PILLARS.length), 2600);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const p = Math.min(1, scrollY / 600);

  return (
    <section
      id="home"
      ref={wrapRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] hero-gradient overflow-hidden grain"
    >
      {/* Parallax industrial background */}
      <div
        className="absolute inset-0 opacity-[0.30] mix-blend-lighten"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=2400&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `translateY(${p * 60}px) scale(${1 + p * 0.05})`,
        }}
        aria-hidden
      />
      <div
        className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full opacity-30 blur-3xl float-slower"
        style={{ background: "radial-gradient(closest-side, rgba(184,134,58,0.55), transparent)" }}
        aria-hidden
      />
      <div
        className="absolute bottom-0 left-0 w-[560px] h-[560px] rounded-full opacity-30 blur-3xl float-slow"
        style={{ background: "radial-gradient(closest-side, rgba(59,130,246,0.35), transparent)" }}
        aria-hidden
      />

      {/* Grid lines */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
        aria-hidden
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-24 lg:pt-44 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — Copy */}
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-4 py-1.5 mb-8 reveal in-view"
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
              <span className="text-white/85 tracking-eyebrow">Soluções Corporativas e Industriais</span>
            </div>

            <h1
              className="font-display text-white text-[46px] sm:text-6xl lg:text-[80px] font-bold leading-[0.98] tracking-[-0.03em] reveal in-view"
              data-testid="hero-title"
            >
              Mantemos operações
              <br />
              <span className="text-white/70">em </span>
              <span className="text-gold">movimento</span>
              <span className="text-white/70">.</span>
            </h1>

            <p
              className="mt-8 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed font-light reveal in-view"
              data-testid="hero-subtitle"
            >
              Somos especialistas em <span className="text-white">soluções corporativas e industriais</span>,
              fornecendo peças, equipamentos, nacionalização de componentes e soluções inteligentes para
              garantir a <span className="text-white">continuidade operacional</span> da sua empresa.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={BRAND.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-primary"
                className="btn-primary text-base"
                aria-label="Solicitar cotação pelo WhatsApp"
              >
                <MessageCircle className="w-5 h-5" />
                Solicitar Cotação
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#solucoes"
                data-testid="hero-cta-secondary"
                className="btn-ghost text-base"
              >
                Conhecer Soluções
              </a>
            </div>

            {/* Signature */}
            <div className="mt-14 flex items-center gap-4 text-white/70">
              <div className="h-px w-14 bg-white/30" />
              <div className="uppercase tracking-[0.28em] text-[10px] font-semibold">
                Imporparts — Soluções Corporativas e Industriais
              </div>
            </div>
          </div>

          {/* RIGHT — Rotating pillar showcase */}
          <div className="lg:col-span-5">
            <div className="relative reveal in-view">
              <div
                className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 lg:p-10 shadow-2xl shadow-black/40"
                data-testid="hero-pillar-card"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-2 text-white/80">
                    <Cog className="w-4 h-4 text-gold" />
                    <span className="tracking-eyebrow">Onde atuamos</span>
                  </div>
                  <div className="text-white/50 text-xs font-mono">
                    {String(pillar + 1).padStart(2, "0")} / {String(PILLARS.length).padStart(2, "0")}
                  </div>
                </div>

                <div className="font-display text-white text-4xl lg:text-[44px] font-bold tracking-tighter leading-[1.05] min-h-[128px]">
                  {PILLARS[pillar]}
                </div>

                <div className="mt-6 text-white/75 leading-relaxed">
                  Garantimos soluções industriais que mantêm operações em movimento com
                  <span className="text-white"> agilidade, confiabilidade e inteligência técnica</span>.
                </div>

                <div className="mt-8 grid grid-cols-4 gap-2">
                  {PILLARS.map((t, i) => (
                    <button
                      key={t}
                      onClick={() => setPillar(i)}
                      aria-label={`Selecionar ${t}`}
                      data-testid={`hero-pillar-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all ${
                        i === pillar ? "bg-gold" : "bg-white/20 hover:bg-white/40"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* floating badge */}
              <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 bg-white text-navy rounded-2xl px-5 py-3 shadow-2xl float-slow">
                <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-bold text-sm">
                  IP
                </div>
                <div>
                  <div className="text-xs text-slate-500">Resposta média</div>
                  <div className="text-sm font-semibold">Cotação em até 24h</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* scroll cue */}
      <a
        href="#institucional"
        aria-label="Rolar para próxima seção"
        data-testid="hero-scroll-cue"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center text-white/60 hover:text-white transition-colors"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Rolar</span>
        <ChevronDown className="w-4 h-4 animate-bounce mt-1" />
      </a>
    </section>
  );
};
